import { useQueryClient } from '@tanstack/react-query';
import type { GetMeResponse } from '@whispers/sdk';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import {
  api,
  clearTokens,
  registerSessionExpiredHandler,
  setAccessToken,
  type MeUser,
  useLogout,
  useMe,
} from '../api.js';

export type AuthState = {
  user: MeUser | undefined;
  me: GetMeResponse | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | undefined;
  refetch: () => Promise<void>;
  logout: () => Promise<void>;
  onSignIn: () => void;
};

export const AuthContext = createContext<AuthState | undefined>(undefined);

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const queryClient = useQueryClient();

  // The HttpOnly refresh cookie restores an in-memory access token at startup.
  // Until it completes, do not cache an unauthenticated /me response.
  const [ready, setReady] = useState(false);
  const [hasToken, setHasToken] = useState(false);

  // Register a handler so withRefresh can signal when both tokens are dead
  // mid-session. Clears React Query cache and drops hasToken so AppLayout
  // redirects the user to sign-in.
  useEffect(() => {
    return registerSessionExpiredHandler(() => {
      setHasToken(false);
      queryClient.removeQueries();
    });
  }, [queryClient]);

  useEffect(() => {
    if (ready) {
      return;
    }

    // Attempt to exchange the HttpOnly refresh cookie for a new access token.
    let cancelled = false;

    api.auth
      .refreshToken()
      .then((data) => {
        if (!cancelled) {
          setAccessToken(data.accessToken);
          setHasToken(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          // Refresh token is expired or invalid — clear everything.
          clearTokens();
          setHasToken(false);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [ready]);

  const {
    data,
    isLoading: meLoading,
    error,
    refetch,
  } = useMe({
    enabled: ready && hasToken,
  });
  const logoutMutation = useLogout();

  const isLoading = !ready || meLoading;

  const value: AuthState = {
    user: data?.user ?? undefined,
    me: data ?? undefined,
    isLoading,
    isAuthenticated: !!data?.user,
    error: error ?? undefined,
    refetch: async () => {
      await refetch();
    },
    logout: async () => {
      await logoutMutation.mutateAsync();
      setHasToken(false);
    },
    onSignIn: () => {
      setHasToken(true);
      setReady(true);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthState => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ─── Mock provider for stories and tests ─────────────────────────────────────

type MockAuthProviderProps = {
  value: Partial<AuthState>;
  children: ReactNode;
};

const defaultMockState: AuthState = {
  user: undefined,
  me: undefined,
  isLoading: false,
  isAuthenticated: false,
  error: undefined,
  refetch: async () => {},
  logout: async () => {},
  onSignIn: () => {},
};

export const MockAuthProvider = ({ value, children }: MockAuthProviderProps) => (
  <AuthContext.Provider value={{ ...defaultMockState, ...value }}>{children}</AuthContext.Provider>
);
